import React, { useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../layouts/App/hooks/useAppSelector';
import { logout } from '../../store/slices/authSlice';
import { useAuth } from '../../services/auth/hooks/signUp';
import { areTokensValid } from '../../services/auth/utils/tokenCache';
import Sidebar from './Sidebar/Sidebar';
import MainContent from './MainContent/MainContent';
import RightPanel from './RightPanel/RightPanel';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userProfile, appSyncUser, isLoggedIn } = useAppSelector((state) => state.auth);
  const { signOut: cognitoSignOut } = useAuth();
  const { tripId, section } = useParams<{ tripId?: string; section?: string }>();
  const location = useLocation();

  // Security: Validate tokens on dashboard mount
  // Prevent access if logged in without valid tokens (except in dev mode with test user)
  useEffect(() => {
    const isTestMode = process.env.NODE_ENV === 'development' && 
                       appSyncUser?.email === 'test@example.com';
    
    if (isLoggedIn && !isTestMode && !areTokensValid()) {
      console.warn('⚠️ User is logged in but tokens are invalid, logging out for security');
      dispatch(logout());
      navigate('/', { replace: true });
    }
  }, [isLoggedIn, appSyncUser, dispatch, navigate]);

  // Derive activeSection from URL pathname
  // Routes structure:
  // - /trips/:tripId -> tripId param exists, activeSection = 'trips'
  // - /:section -> section param exists (overview, trips, plan, settings)
  // - fallback to 'overview'
  const getActiveSection = (): string => {
    // If tripId exists, we're viewing a trip detail page
    if (tripId) {
      return 'trips';
    }
    
    // Use section from URL params (e.g., /overview, /plan, /settings, /trips)
    if (section) {
      // Validate that section is a valid dashboard section
      const validSections = ['overview', 'trips', 'plan', 'settings'];
      if (validSections.includes(section)) {
        return section;
      }
    }
    
    // Fallback: extract from pathname if section param is not available
    const pathParts = location.pathname.split('/').filter(part => part);
    if (pathParts.length > 0) {
      const firstPart = pathParts[0];
      const validSections = ['overview', 'trips', 'plan', 'settings'];
      if (validSections.includes(firstPart)) {
        return firstPart;
      }
    }
    
    return 'overview';
  };

  const activeSection = getActiveSection();

  const handleLogout = async () => {
    try {
      await cognitoSignOut();
      dispatch(logout());
    } catch (error) {
      console.error('Logout failed:', error);
      dispatch(logout());
    }
  };

  return (
    <div className="dashboard">
      <Sidebar 
        activeSection={activeSection}
      />
      
      <MainContent 
        activeSection={activeSection}
        user={userProfile}
        appSyncUser={appSyncUser}
        onLogout={handleLogout}
        tripId={tripId}
      />
      
      <RightPanel 
        user={userProfile}
        appSyncUser={appSyncUser}
      />
    </div>
  );
};

export default Dashboard;
